/**
 * Audit Rail Controller: keeps the left-column
 * transcript indicators in sync with the user's
 * perceived progress in the stage.
 *
 * Source of truth: the sibling `audit-stage`
 * controller dispatches `audit-stage:transition`
 * events whenever it advances stages, swaps in
 * revealed content, or learns the audit failed.
 * The rail listens for those events and recomputes
 * each row's `data-audit-rail-status` attribute.
 *
 * Why we don't read server status broadcasts:
 * the server can finish all six steps before the
 * user has visually moved off step 1. Mirroring
 * server status would race the rail past what's
 * actually on screen, breaking the illusion that
 * the audit is happening live.
 *
 * Rules per row, given the stage's currentKey:
 *
 *  - Row before currentKey  → "done"
 *  - Row at currentKey      → "running"
 *  - Row after currentKey   → "pending"
 *
 * Painting_demo masking: while the user is parked
 * on the report (currentKey == "reading_menu" and
 * reportAcknowledgedAt is blank), painting_demo
 * stays "pending" even if it's running/done in the
 * background. This is redundant with the
 * before/at/after rule (painting_demo is "after"
 * reading_menu) but kept explicit for clarity.
 *
 * Failure override: if the audit is failed, the row
 * whose own per-step server status is "failed" wins:
 * we tag it "failed" regardless of its position vs
 * currentKey. The other rows keep their stage-
 * derived status.
 */
import { Controller } from "@hotwired/stimulus";

const STEP_KEYS = [
  "locating",
  "reading_reviews",
  "picking_photo",
  "sampling_brand",
  "reading_menu",
  "painting_demo",
];

export default class extends Controller {
  static targets = ["row"];

  static values = {
    locatingStatus: { type: String, default: "pending" },
    locatingFinishedAt: { type: String, default: "" },
    readingReviewsStatus: {
      type: String,
      default: "pending",
    },
    readingReviewsFinishedAt: {
      type: String,
      default: "",
    },
    pickingPhotoStatus: {
      type: String,
      default: "pending",
    },
    pickingPhotoFinishedAt: {
      type: String,
      default: "",
    },
    samplingBrandStatus: {
      type: String,
      default: "pending",
    },
    samplingBrandFinishedAt: {
      type: String,
      default: "",
    },
    readingMenuStatus: {
      type: String,
      default: "pending",
    },
    readingMenuFinishedAt: {
      type: String,
      default: "",
    },
    paintingDemoStatus: {
      type: String,
      default: "pending",
    },
    paintingDemoFinishedAt: {
      type: String,
      default: "",
    },
    auditStatus: { type: String, default: "pending" },
    reportAcknowledgedAt: {
      type: String,
      default: "",
    },
  };

  connect() {
    this.handleTransition = (event) => {
      this.recompute(event.detail);
    };
    document.addEventListener(
      "audit-stage:transition",
      this.handleTransition
    );

    // Stage controller may not be connected yet: try
    // a synchronous seed from its current state, and
    // fall back to the server status for first paint.
    const seed = this.seedFromStage();
    this.recompute(seed);
  }

  disconnect() {
    document.removeEventListener(
      "audit-stage:transition",
      this.handleTransition
    );
  }

  // -------- core --------

  recompute(detail) {
    const currentKey = detail?.currentKey ?? null;
    const auditStatus =
      detail?.auditStatus ?? this.auditStatusValue;
    const reportAcknowledgedAt =
      detail?.reportAcknowledgedAt ??
      this.reportAcknowledgedAtValue;

    STEP_KEYS.forEach((key) => {
      const row = this.rowFor(key);
      if (!row) return;
      const status = this.statusForRow(
        key,
        currentKey,
        auditStatus,
        reportAcknowledgedAt
      );
      row.dataset.auditRailStatus = status;
    });
  }

  statusForRow(
    key,
    currentKey,
    auditStatus,
    reportAcknowledgedAt
  ) {
    // Failure is the only signal that overrides the
    // stage's perceived progress. The user MUST see
    // which step blew up.
    if (
      auditStatus === "failed" &&
      this.serverStatusFor(key) === "failed"
    ) {
      return "failed";
    }

    if (
      key === "painting_demo" &&
      !reportAcknowledgedAt
    ) {
      return "pending";
    }

    if (currentKey == null) {
      // No transition has fired yet AND the stage
      // controller isn't queryable. Fall back to the
      // server's per-step status so first paint isn't
      // blank: the next transition event will
      // reconcile.
      return this.serverStatusFor(key);
    }

    const currentIdx = STEP_KEYS.indexOf(currentKey);
    const rowIdx = STEP_KEYS.indexOf(key);
    if (currentIdx < 0 || rowIdx < 0) return "pending";

    if (rowIdx < currentIdx) return "done";
    if (rowIdx === currentIdx) return "running";
    return "pending";
  }

  seedFromStage() {
    const stageEl = document.getElementById(
      this.stageElementId()
    );
    if (!stageEl) return {};
    const stageController =
      this.application.getControllerForElementAndIdentifier(
        stageEl,
        "audit-stage"
      );
    if (!stageController) return {};
    return {
      currentKey: stageController.currentKey,
      swappedKeys: Array.from(
        stageController.swappedKeys ?? []
      ),
      auditStatus: stageController.auditStatusValue,
      reportAcknowledgedAt:
        stageController.reportAcknowledgedAtValue,
    };
  }

  stageElementId() {
    // Rail wrapper id is `audit-rail-<id>`; stage
    // wrapper id is `audit-stage-<id>`. Mirror.
    const match = this.element.id.match(
      /^audit-rail-(.+)$/
    );
    return match ? `audit-stage-${match[1]}` : "";
  }

  serverStatusFor(key) {
    switch (key) {
      case "locating":
        return this.locatingStatusValue;
      case "reading_reviews":
        return this.readingReviewsStatusValue;
      case "picking_photo":
        return this.pickingPhotoStatusValue;
      case "sampling_brand":
        return this.samplingBrandStatusValue;
      case "reading_menu":
        return this.readingMenuStatusValue;
      case "painting_demo":
        return this.paintingDemoStatusValue;
      default:
        return "pending";
    }
  }

  rowFor(key) {
    return this.rowTargets.find(
      (el) => el.dataset.auditRailStepKey === key
    );
  }
}
